import { type Blob } from '@consigli/types';
import { File as FileIcon } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { truncateSourceName } from './utils';

interface SourceListProps {
  onSourceClick: (blob: Blob) => void;
  sources: Blob[];
}

/**
 * The documents behind an answer, listed in the info tooltip itself.
 *
 * What the info icon opened before context was a feature, and what it opens
 * again while `PropChatContext` is off: the sources list that replaced this
 * shares its modal and its rows with add-context, so the two ship together.
 *
 * Documents only - a findings citation has nothing to open here.
 */
export const SourceList: FC<SourceListProps> = ({ onSourceClick, sources }) => {
  const { t } = useTranslation();

  return (
    <div className="w-[208px] overflow-hidden rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] shadow-[0_4px_3px_rgba(0,0,0,0.05),0_2px_2px_rgba(0,0,0,0.05)]">
      <div className="flex w-full items-center px-[var(--ds-space-3)] py-[var(--ds-space-1-5)] font-[var(--ds-typography-font-definitions-font-family-body)] text-[10px] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)] text-[var(--ds-color-text-secondary)]">
        {t('prop-chat.source-list', { count: sources.length })}
      </div>
      <div className="max-h-[var(--ds-space-48)] overflow-y-auto">
        {sources.map((blob) => (
          <button
            type="button"
            key={blob.id}
            aria-label={blob.name}
            title={blob.name}
            onClick={() => onSourceClick(blob)}
            className="flex min-h-[var(--ds-space-8)] w-full cursor-pointer items-center gap-[var(--ds-space-1-5)] border-x-0 border-b border-t-0 border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] px-[var(--ds-space-3)] py-[var(--ds-space-2)] text-left text-[var(--ds-color-text-secondary)] last:border-b-0 hover:bg-[var(--ds-color-bg-secondary)] focus-visible:bg-[var(--ds-color-bg-secondary)] focus-visible:outline-none"
          >
            <FileIcon aria-hidden="true" className="size-[var(--ds-space-3)] shrink-0" />
            <span className="min-w-0 flex-1 whitespace-nowrap font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-mini-font-size)] font-[var(--ds-typography-paragraph-paragraph-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)]">
              {truncateSourceName(blob.name)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
